import React from 'react';
import Reveal from './Reveal';
import fitpulseLogo from '../assets/fitpulsebot.png';
import { PRODUCTS } from '../config';

const MAIN_PRODUCTS = [
  {
    title: 'FitPulseBot',
    status: 'Live · Production',
    statusColor: 'brand-green',
    desc: 'AI-powered health & fitness tracking SaaS ecosystem with comprehensive insights, personalized recommendations, and smart habit coaching.',
    features: ['AI Health Analytics', 'Fitness Tracking', 'Nutrition Guidance', 'Progress Insights'],
    badge: 'Featured Product',
    badgeBg: 'bg-brand-green/20 text-brand-green border-brand-green/30',
    logo: fitpulseLogo,
    accent: 'brand-green',
    gradient: 'from-brand-green/10 to-brand-cyan/5',
  },
  {
    title: 'AI Visual Teacher',
    status: 'In Development',
    statusColor: 'brand-blue',
    desc: 'Revolutionary platform converting textbooks into AI-powered interactive video lessons — personalized, engaging, and adaptive learning for every student.',
    features: ['Text-to-Video AI', 'Interactive Learning', 'Personalized Content', 'Progress Tracking'],
    badge: 'Coming Soon',
    badgeBg: 'bg-brand-blue/20 text-brand-blue border-brand-blue/30',
    icon: '📚',
    accent: 'brand-blue',
    gradient: 'from-brand-blue/10 to-brand-purple/5',
  },
  {
    title: 'AI Marketing SaaS',
    status: 'Prototype Stage',
    statusColor: 'brand-gold',
    desc: 'Automated digital marketing powered by AI — campaign optimization, audience targeting, content generation, and ROI maximization on autopilot.',
    features: ['Campaign Automation', 'AI Analytics', 'Audience Targeting', 'ROI Optimization'],
    badge: 'In Development',
    badgeBg: 'bg-brand-gold/20 text-brand-gold border-brand-gold/30',
    icon: '📈',
    accent: 'brand-gold',
    gradient: 'from-brand-gold/10 to-brand-pink/5',
  },
  {
    title: 'Nirvanta Naturals',
    status: 'Concept Stage',
    statusColor: 'brand-green',
    desc: 'Wellness and food product line — pure, sustainable, scientifically crafted natural health products for modern conscious consumers.',
    features: ['Herbal Powders', 'Health Supplements', 'Natural Teas', 'Eco Packaging'],
    badge: 'Concept',
    badgeBg: 'bg-brand-purple/20 text-brand-purple border-brand-purple/30',
    icon: '🌿',
    accent: 'brand-green',
    gradient: 'from-brand-green/10 to-brand-cyan/5',
  },
];

const UPCOMING = [
  { icon: '🎯', title: 'Education & Career Guidance AI', desc: 'Personalized career path recommendations and skill development roadmaps powered by AI.' },
  { icon: '⚡', title: 'Business Productivity Automation', desc: 'AI-driven workflow automation and productivity enhancement for modern enterprises.' },
  { icon: '🚀', title: 'Skill Development Platform', desc: 'Interactive learning with AI-powered skill assessment and personalised curricula.' },
];

const Products = () => (
  <section id="products" className="section-padding bg-brand-surface relative overflow-hidden scroll-mt-16">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-brand-purple/5 blur-[140px] pointer-events-none" />

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

      <Reveal from="up" className="section-header">
        <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-sm font-medium text-brand-gold border border-brand-gold/20 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-gold" />
          What We're Building
        </div>
        <h2 className="text-4xl md:text-6xl font-black text-brand-text mb-5">
          Our <span className="text-gradient">Products</span>
        </h2>
        <p className="text-lg text-brand-muted max-w-2xl mx-auto">
          AI-powered products reshaping industries — from health & fitness to education, marketing, and wellness.
        </p>
      </Reveal>

      {/* Featured Product */}
      <Reveal from="up" className="mb-6">
        <div className="card gradient-border relative overflow-hidden group hover:shadow-glow-blue transition-all duration-500">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-green/8 to-brand-cyan/5 rounded-2xl" />
          <div className="relative z-10 flex flex-col md:flex-row gap-6 md:gap-10 items-start md:items-center">
            {/* Logo + badge */}
            <div className="flex-shrink-0">
              <div className="flex items-center gap-4 mb-4">
                <img src={fitpulseLogo} alt="FitPulseBot" className="h-16 md:h-20 w-auto object-contain" />
                <div>
                  <span className={`tag bg-brand-green/20 text-brand-green border border-brand-green/30`}>🔴 Live Product</span>
                  <h3 className="text-2xl md:text-3xl font-black text-brand-text mt-2">FitPulseBot</h3>
                  <p className="text-brand-green text-sm font-semibold">AI Health & Fitness SaaS</p>
                </div>
              </div>
              <a
                href={PRODUCTS.FITPULSEBOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-sm py-2.5 px-5"
              >
                Visit Product
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
            {/* Details */}
            <div className="flex-1">
              <p className="text-brand-muted leading-relaxed mb-5">
                AI-powered health & fitness tracking SaaS ecosystem with comprehensive insights, personalized recommendations, and smart habit coaching. Built from the ground up with real AI at its core.
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                {MAIN_PRODUCTS[0].features.map((f, fi) => (
                  <div key={fi} className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-brand-green flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-brand-muted text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Other Products */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
        {MAIN_PRODUCTS.slice(1).map((p, i) => (
          <Reveal key={i} from="up" delay={i * 80}>
            <div className={`card group relative overflow-hidden hover:shadow-glow-purple transition-all duration-300 h-full`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${p.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-4xl">{p.icon}</div>
                  <span className={`tag text-xs ${p.badgeBg} border`}>{p.badge}</span>
                </div>
                <h3 className="text-brand-text font-bold text-lg mb-2">{p.title}</h3>
                <p className={`text-${p.statusColor} text-xs font-semibold mb-3`}>{p.status}</p>
                <p className="text-brand-muted text-sm leading-relaxed mb-4">{p.desc}</p>
                <div className="space-y-1.5">
                  {p.features.map((f, fi) => (
                    <div key={fi} className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full bg-${p.accent} flex-shrink-0`} />
                      <span className="text-brand-muted text-xs">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Upcoming */}
      <Reveal from="up">
        <div className="glass rounded-2xl p-8 border border-brand-border">
          <h3 className="text-2xl font-bold text-brand-text mb-2 text-center">Upcoming AI-Driven Tools</h3>
          <p className="text-brand-muted text-sm text-center mb-8">More products in the pipeline — built with AI at every layer.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {UPCOMING.map((u, i) => (
              <div key={i} className="bg-brand-card rounded-xl p-5 border border-brand-border hover:border-brand-purple/30 transition-all duration-300 group">
                <div className="text-3xl mb-3 group-hover:scale-110 transition-transform duration-300">{u.icon}</div>
                <h4 className="text-brand-text font-semibold mb-2 text-sm">{u.title}</h4>
                <p className="text-brand-muted text-xs leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Products;
